import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useId, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import copy from 'copy-to-clipboard'

import {
   colors,
   IconButton,
   IconButtonHierarchy,
   IconButtonSubVariant,
   TextField
} from '@flowwai-frontend/design-system'
import { i18nTProp } from '@flowwai-frontend/shared/common-components'
import { CopyIcon3, XCloseIcon } from '@flowwai-frontend/design-system/icons'
import {
   validateLatitude,
   validateLongitude
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { MapCoordinates } from '../../types'
import { imageUrls } from '../../constants/imageConstants'

import * as Styles from './styles'

interface Props {
   mapsT: i18nTProp
   isMobile: boolean
   value: MapCoordinates | null
   onChangeValue: (value: MapCoordinates) => void
   showCRMSuccessToast?: (message: string) => void
   position: number
   onDelete: () => void
   isReadOnly: boolean
   containerClassName?: string
   showCopyCoordinateAction?: boolean
   hideMarkerNumber?: boolean
   onClose?: () => void
}

const CoordinateInput = (props: Props): React.ReactElement => {
   const {
      isMobile,
      value,
      onChangeValue,
      onDelete,
      isReadOnly,
      containerClassName,
      showCopyCoordinateAction,
      hideMarkerNumber
   } = props

   const [latitude, setLatitude] = useState<string | null>(
      value?.lat.toString() ?? null
   )
   const [hasLatitudeError, setHasLatitudeError] = useState<boolean>(false)

   const [longitude, setLongitude] = useState<string | null>(
      value?.lng.toString() ?? null
   )
   const [hasLongitudeError, setHasLongitudeError] = useState<boolean>(false)

   const t = props.mapsT

   useEffect(() => {
      setLatitude(value?.lat.toString() ?? null)
      setLongitude(value?.lng.toString() ?? null)
      setHasLatitudeError(false)
      setHasLongitudeError(false)
   }, [`${value?.lat}:${value?.lng}`])

   const copyTooltipId = useId()

   const onValidateLatitude = (latitudeValue: number | null): boolean => {
      const isValidLatitude = latitudeValue
         ? validateLatitude(latitudeValue)
         : false

      if (!isValidLatitude) {
         setHasLatitudeError(true)
         return false
      }

      return true
   }

   const onValidateLongitude = (longitudeValue: number | null): boolean => {
      const isValidLongitude = longitudeValue
         ? validateLongitude(longitudeValue)
         : false

      if (!isValidLongitude) {
         setHasLongitudeError(true)
         return false
      }

      return true
   }

   const onBlurLatitude = () => {
      const latitudeValue = latitude ? Number(latitude) : null
      const isValidLatitude = onValidateLatitude(latitudeValue)

      if (!isValidLatitude) return

      setHasLatitudeError(false)

      const longitudeValue = longitude ? Number(longitude) : null

      const isValidLongitude = onValidateLongitude(longitudeValue)

      if (!isValidLongitude) return

      const areBothValuesPresent =
         latitudeValue !== null && longitudeValue !== null

      if (!areBothValuesPresent) return

      onChangeValue({
         lat: latitudeValue,
         lng: longitudeValue
      })
   }

   const onBlurLongitude = () => {
      const longitudeValue = longitude ? Number(longitude) : null
      const isValidLongitude = onValidateLongitude(longitudeValue)

      if (!isValidLongitude) return

      setHasLongitudeError(false)

      const latitudeValue = latitude ? Number(latitude) : null
      const isValidLatitude = onValidateLatitude(latitudeValue)

      if (!isValidLatitude) return

      const areBothValuesPresent =
         latitudeValue !== null && longitudeValue !== null
      if (!areBothValuesPresent) return

      onChangeValue({
         lat: latitudeValue,
         lng: longitudeValue
      })
   }

   const degrees = '\u00B0'

   const renderDegrees = (suffix: string): React.ReactElement => (
      <span
         className={cn('text-sm-medium', 'text-gray-400')}
      >{`${degrees}${suffix}`}</span>
   )

   const renderLatitude = (): React.ReactElement => (
      <div
         className={cn(
            Styles.coordinateInputContainerStyles,
            isMobile ? 'px-3 py-2' : '',
            hasLatitudeError ? 'border-error-300' : 'border-gray-300'
         )}
         title={latitude?.toString()}
      >
         <TextField
            value={latitude?.toString() ?? ''}
            onChange={setLatitude}
            onBlur={onBlurLatitude}
            size='ExtraSmall'
            placeholder={'Latitude'}
            rightElement={() => renderDegrees('N')}
            containerClassName='w-full'
            inputContainerClassName={cn(Styles.inputContainerStyles)}
            inputClassName={Styles.inputStyles}
            focusRingVariant={'None'}
            isDisabled={isReadOnly}
         />
      </div>
   )

   const renderLongitude = (): React.ReactElement => (
      <div
         className={cn(
            Styles.coordinateInputContainerStyles,
            isMobile ? 'px-3 py-2' : '',
            hasLongitudeError ? 'border-error-300' : 'border-gray-300'
         )}
         title={longitude?.toString()}
      >
         <TextField
            value={longitude?.toString() ?? ''}
            onChange={setLongitude}
            size='ExtraSmall'
            placeholder={'Longitude'}
            rightElement={() => renderDegrees('E')}
            containerClassName='w-full'
            inputContainerClassName={cn(Styles.inputContainerStyles)}
            inputClassName={Styles.inputStyles}
            focusRingVariant={'None'}
            onBlur={onBlurLongitude}
            isDisabled={isReadOnly}
         />
      </div>
   )

   const renderTooltip = () => (
      <Tooltip
         content={t('copy')}
         className='!bg-gray-900 !opacity-[1] rounded-[8px] text-xs-semibold'
         id={copyTooltipId}
      />
   )

   const onClickCopyCoordinates = (): void => {
      if (value) {
         copy(`${value.lat},${value.lng}`)
         props.showCRMSuccessToast?.(t('copySuccessMessage'))
      }
   }

   const renderCopyCoordinateIcon = () => (
      <div data-tooltip-id={copyTooltipId}>
         <IconButton
            hierarchy={IconButtonHierarchy.Tertiary}
            subVariant={IconButtonSubVariant.Gray}
            size={'Small'}
            icon={({ stroke }) => <CopyIcon3 fill={stroke} />}
            onClick={onClickCopyCoordinates}
         />
         {renderTooltip()}
      </div>
   )

   const renderRemoveIcon = (): React.ReactElement => (
      <button onClick={onDelete}>
         <XCloseIcon fill={colors['error-700']} height={16} width={16} />
      </button>
   )

   const renderCloseIcon = (): React.ReactElement | null => (
      <div className='px-1'>
         <IconButton
            size={'Small'}
            icon={({ stroke }) => (
               <XCloseIcon fill={stroke} height={16} width={16} />
            )}
            hierarchy={IconButtonHierarchy.Tertiary}
            subVariant={IconButtonSubVariant.Gray}
            onClick={props.onClose}
         />
      </div>
   )

   const hasError = hasLatitudeError || hasLongitudeError

   const renderMarkerNumber = (): React.ReactElement => (
      <div className='flex relative'>
         <img className='h-[22px]' src={imageUrls.mapMarker} />
         <p className={cn(Styles.markerNumberStyles, 'text-base-white')}>
            {props.position}
         </p>
      </div>
   )

   return (
      <>
         <div className={cn('flex items-center gap-2', containerClassName)}>
            {hideMarkerNumber ? null : renderMarkerNumber()}

            {renderLatitude()}
            {renderLongitude()}
            <div className='flex items-center'>
               {isReadOnly ? null : renderRemoveIcon()}
               {showCopyCoordinateAction ? renderCopyCoordinateIcon() : null}
               {props.onClose ? renderCloseIcon() : null}
            </div>
         </div>
         {hasError ? (
            <p className={cn('text-xs-regular pl-8', 'text-error-500')}>
               {t('enterValidCoordinates')}
            </p>
         ) : null}
      </>
   )
}

export default observer(CoordinateInput)
